import { useEffect } from "react";
import { ShieldAlert, ClipboardCheck, Database, Scale } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Card } from "@/components/ui/card";
import ClinicianBriefDownload from "@/components/marketing/ClinicianBriefDownload";
import ClinicianInquiryForm from "@/components/marketing/ClinicianInquiryForm";

const META_TITLE = "Clinician information under review | Myelina Health";
const META_DESCRIPTION =
  "Pre-release clinician information for Myelina Health. Public collection, enrollment, patient-data workflows, and clinician previews are paused while evidence is reviewed.";

const setMeta = (name: string, content: string) => {
  let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
  return element;
};

const ForClinicians = () => {
  useEffect(() => {
    const previousTitle = document.title;
    const existingDescription = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const existingRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const previousDescription = existingDescription?.getAttribute("content") ?? null;
    const previousRobots = existingRobots?.getAttribute("content") ?? null;

    document.title = META_TITLE;
    const description = setMeta("description", META_DESCRIPTION);
    const robots = setMeta("robots", "noindex, follow");

    return () => {
      document.title = previousTitle;
      if (previousDescription === null) description.remove();
      else description.setAttribute("content", previousDescription);
      if (previousRobots === null) robots.remove();
      else robots.setAttribute("content", previousRobots);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main id="main-content" className="pt-32 pb-0 sm:pt-40">
        <section className="container mx-auto px-6 sm:px-10 lg:px-16">
          <div className="mx-auto max-w-3xl text-center">
            <div className="eyebrow justify-center mx-auto">For clinical teams</div>
            <h1 className="mt-6 font-display text-display-lg text-foreground">
              Clinician information is under evidence review.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-foreground/80">
              Public clinician enrollment, contact submission, credential upload, patient-data sharing, roster previews, cohort reporting, and pilot offers are paused. This page describes the source-level hold and what must be verified before any clinician program can open.
            </p>
          </div>

          <Card className="mx-auto mt-12 max-w-4xl border-primary/20 bg-primary/5 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <ShieldAlert className="mt-1 h-6 w-6 flex-none text-primary" aria-hidden="true" />
              <div>
                <h2 className="font-display text-2xl text-foreground">Current public status</h2>
                <p className="mt-3 text-[15px] leading-7 text-foreground/85">
                  No public form on this page sends data. No public route displays patient details or an operational clinician workspace. No public copy should be treated as proof of production security, privacy, clinical validity, regulatory status, or legal compliance.
                </p>
              </div>
            </div>
          </Card>
        </section>

        <section aria-labelledby="source-boundary-heading" className="container mx-auto px-6 py-20 sm:px-10 lg:px-16 sm:py-24">
          <div className="mx-auto max-w-5xl">
            <h2 id="source-boundary-heading" className="text-center font-display text-3xl text-foreground sm:text-4xl">
              Source boundary in this release candidate
            </h2>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              <Card className="border-border/60 bg-card p-6 sm:p-7">
                <ClipboardCheck className="h-5 w-5 text-primary" aria-hidden="true" />
                <h3 className="mt-4 font-display text-xl text-foreground">Retrospective wellness only</h3>
                <p className="mt-3 text-sm leading-6 text-foreground/80">
                  Logged patterns may be incomplete. They do not prove cause or predict what happens next. The public boundary does not diagnose, recommend treatment, monitor patients, or provide emergency support.
                </p>
              </Card>
              <Card className="border-border/60 bg-card p-6 sm:p-7">
                <Database className="h-5 w-5 text-primary" aria-hidden="true" />
                <h3 className="mt-4 font-display text-xl text-foreground">No public clinical data flow</h3>
                <p className="mt-3 text-sm leading-6 text-foreground/80">
                  Public clinician contact collection, credential files, patient information, roster views, summaries, exports, and cohort reports are disabled or replaced with no-data holding views.
                </p>
              </Card>
              <Card className="border-border/60 bg-card p-6 sm:p-7">
                <Scale className="h-5 w-5 text-primary" aria-hidden="true" />
                <h3 className="mt-4 font-display text-xl text-foreground">No public offer or assurance</h3>
                <p className="mt-3 text-sm leading-6 text-foreground/80">
                  No price, pilot duration, cohort size, benefit, BAA, DPA, response time, data location, encryption, authorization, deletion, or subprocessor statement is offered here as an established fact.
                </p>
              </Card>
            </div>
          </div>
        </section>

        <section aria-labelledby="evidence-heading" className="bg-secondary py-20 sm:py-24">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16">
            <div className="mx-auto max-w-4xl">
              <h2 id="evidence-heading" className="text-center font-display text-3xl text-foreground sm:text-4xl">
                Evidence required before a clinician program can open
              </h2>
              <div className="mt-10 grid gap-5 sm:grid-cols-2">
                {[
                  "An exact deployed-revision inventory of processors, regions, data categories, routes, logs, analytics, retention, backups, export, deletion, authentication, and communication providers.",
                  "Authenticated synthetic testing of roles, row-level isolation, invitation controls, consent and withdrawal, sharing, audit evidence, incident paths, and rollback procedures.",
                  "Versioned Privacy and Terms notices that match deployed behavior, with owner, effective date, change history, and archival location.",
                  "Qualified privacy, legal, security, accessibility, clinical, claims, regulatory, and trademark review for the proposed use and jurisdictions.",
                  "A written scope defining owners, approvals, representative tests, support boundaries, disable procedures, acceptance criteria, and commercial terms.",
                  "Post-deployment verification tied to the released commit. Green source checks alone do not establish production behavior or compliance.",
                ].map((item) => (
                  <Card key={item} className="border-border/60 bg-card p-5 text-sm leading-6 text-foreground/80">
                    {item}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="brief-heading" className="container mx-auto px-6 py-20 sm:px-10 lg:px-16 sm:py-24">
          <h2 id="brief-heading" className="sr-only">Local clinician information brief</h2>
          <ClinicianBriefDownload />
        </section>

        <section id="clinician-inquiry" aria-labelledby="local-outline-heading" className="bg-secondary py-20 sm:py-24">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16">
            <div className="mx-auto mb-9 max-w-2xl text-center">
              <h2 id="local-outline-heading" className="font-display text-3xl text-foreground sm:text-4xl">
                Outline a general question without submitting it
              </h2>
              <p className="mt-4 text-[15px] leading-7 text-foreground/80">
                The checklist below is local-only. It clears on review and has no network, storage, upload, analytics, or outreach path.
              </p>
            </div>
            <ClinicianInquiryForm />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ForClinicians;
